import React, { useState, useEffect } from "react";
import { holidayAPI, formatError } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import { Plus, Edit, Trash2, X, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useConfirm } from "../hooks/useConfirm";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const FULL_MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const TYPES = [
  { value: "national", label: "National Holiday", color: "bg-red-100 text-red-700" },
  { value: "state", label: "State Holiday", color: "bg-orange-100 text-orange-700" },
  { value: "local", label: "Local/School Holiday", color: "bg-yellow-100 text-yellow-700" },
];

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[92vh] overflow-y-auto animate-fade-in-up">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB] sticky top-0 bg-white">
          <h2 className="font-bold text-lg font-[Manrope] text-[#3A322C]">{title}</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export default function HolidayCalendar() {
  const { user } = useAuth();
  const isAdmin = user?.is_admin;
  const now = new Date();
  const { confirm, ConfirmModal } = useConfirm();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-indexed
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => { fetchHolidays(); }, [year]);

  const fetchHolidays = async () => {
    setLoading(true);
    try {
      const r = await holidayAPI.getHolidays({ year });
      setHolidays(r.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const monthHolidays = holidays.filter(h => {
    const hMonth = new Date(h.date + "T00:00:00").getMonth();
    return hMonth === month;
  });

  const holidayDates = new Set(holidays.map(h => h.date));

  // Build calendar
  const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const calDays = [];
  for (let i = 0; i < firstDay; i++) calDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) calDays.push(d);

  const getHolidayForDay = (d) => {
    if (!d) return null;
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    return holidays.find(h => h.date === dateStr);
  };

  const handleSave = async () => {
    setError("");
    try {
      if (modal.mode === "create") await holidayAPI.createHoliday(form);
      else await holidayAPI.updateHoliday(modal.id, form);
      setModal(null);
      fetchHolidays();
      setSuccess("Saved!"); setTimeout(() => setSuccess(""), 2000);
    } catch (e) { setError(formatError(e)); }
  };

  const handleDelete = async (id) => {
    if (!await confirm("Delete this holiday?")) return;
    await holidayAPI.deleteHoliday(id);
    fetchHolidays();
  };

  const getTypeConfig = (type) => TYPES.find(t => t.value === type) || TYPES[2];

  return (
    <div className="space-y-6 max-w-5xl" data-testid="holiday-calendar">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">School Holiday Calendar</h1>
          <p className="text-sm text-[#8A7E73] mt-0.5">Marks non-serving days · Used in billing to calculate actual working days</p>
        </div>
        {isAdmin && (
          <button onClick={() => { setForm({ holiday_type: "national", applies_to: "all", is_school_holiday: true }); setModal({ mode: "create" }); setError(""); }}
            className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 font-bold text-sm flex items-center gap-1.5">
            <Plus size={14} /> Add Holiday
          </button>
        )}
      </div>

      {success && <div className="bg-[#EDF2EE] border border-[#637C64]/30 rounded-lg px-4 py-2.5 text-sm text-[#637C64]">{success}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2 bg-white border border-[#E5E1DB] rounded-2xl overflow-hidden shadow-sm">
          {/* Calendar header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E1DB]">
            <button onClick={() => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); }} className="p-2 hover:bg-[#F4F3F0] rounded-lg"><ChevronLeft size={18} /></button>
            <h2 className="font-bold text-[#3A322C] text-lg font-[Manrope]">{FULL_MONTHS[month]} {year}</h2>
            <button onClick={() => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); }} className="p-2 hover:bg-[#F4F3F0] rounded-lg"><ChevronRight size={18} /></button>
          </div>
          <div className="p-4">
            {/* Year navigation */}
            <div className="flex items-center justify-center gap-4 mb-4">
              <button onClick={() => setYear(y => y - 1)} className="text-sm text-[#8A7E73] hover:text-[#3A322C]">← {year - 1}</button>
              <span className="font-bold text-[#E8956D]">{year}</span>
              <button onClick={() => setYear(y => y + 1)} className="text-sm text-[#8A7E73] hover:text-[#3A322C]">{year + 1} →</button>
            </div>
            {/* Day headers */}
            <div className="grid grid-cols-7 mb-2">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => (
                <div key={d} className={`text-center text-xs font-bold py-2 ${d === "Sun" ? "text-[#C2624E]" : "text-[#8A7E73]"}`}>{d}</div>
              ))}
            </div>
            {/* Days */}
            <div className="grid grid-cols-7 gap-1">
              {calDays.map((d, i) => {
                const holiday = getHolidayForDay(d);
                const isSunday = d && new Date(year, month, d).getDay() === 0;
                const isToday = d && year === now.getFullYear() && month === now.getMonth() && d === now.getDate();
                return (
                  <div key={`idx-${i}`} className={`
                    aspect-square flex flex-col items-center justify-center rounded-xl text-sm cursor-default relative
                    ${!d ? "" : isToday ? "bg-[#E8956D] text-white font-bold" : holiday ? "bg-red-50 border border-red-200" : isSunday ? "bg-gray-50 text-[#C4BAAF]" : "hover:bg-[#F4F3F0]"}
                  `} title={holiday?.name || ""}>
                    {d && <span className={`text-xs font-medium ${isToday ? "text-white" : isSunday ? "text-[#C4BAAF]" : "text-[#3A322C]"}`}>{d}</span>}
                    {holiday && <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-red-500" />}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Month's holidays list */}
        <div className="bg-white border border-[#E5E1DB] rounded-2xl overflow-hidden shadow-sm">
          <div className="px-5 py-4 border-b border-[#E5E1DB] bg-[#FAFAF8]">
            <h3 className="font-bold text-[#3A322C]">{FULL_MONTHS[month]} Holidays</h3>
            <p className="text-xs text-[#8A7E73]">{monthHolidays.length} holiday(s)</p>
          </div>
          <div className="divide-y divide-[#F4F3F0] max-h-80 overflow-y-auto">
            {monthHolidays.length === 0 ? (
              <div className="text-center py-8 text-[#8A7E73] text-sm">No holidays this month</div>
            ) : monthHolidays.map(h => {
              const typeConf = getTypeConfig(h.holiday_type);
              return (
                <div key={h.id} className="px-4 py-3 flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="font-medium text-[#3A322C] text-sm">{h.name}</p>
                    <p className="text-xs text-[#8A7E73]">{new Date(h.date + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric" })}</p>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${typeConf.color}`}>{typeConf.label}</span>
                  </div>
                  {isAdmin && (
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => { setForm({ ...h }); setModal({ mode: "edit", id: h.id }); setError(""); }} className="p-1 text-[#8A7E73] hover:text-[#E8956D]"><Edit size={13} /></button>
                      <button onClick={() => handleDelete(h.id)} className="p-1 text-[#8A7E73] hover:text-[#C2624E]"><Trash2 size={13} /></button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* Full year summary */}
          <div className="px-4 py-3 border-t border-[#E5E1DB] bg-[#F4F3F0]">
            <p className="text-xs font-bold text-[#5C5147]">Full Year {year}: {holidays.length} holidays</p>
          </div>
        </div>
      </div>

      {/* All holidays table */}
      <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#F4F3F0] bg-[#FAFAF8] flex items-center justify-between">
          <h3 className="font-bold text-[#3A322C]">All Holidays — {year}</h3>
          <span className="text-xs text-[#8A7E73]">{holidays.length} total</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F3F0]">
              <tr>{["Date", "Day", "Holiday Name", "Type", "Affects Billing", "Actions"].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase text-[#5C5147]">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-[#F4F3F0]">
              {holidays.map(h => {
                const d = new Date(h.date + "T00:00:00");
                const typeConf = getTypeConfig(h.holiday_type);
                return (
                  <tr key={h.id} className="hover:bg-[#FAFAF8]">
                    <td className="px-4 py-3 font-bold text-[#E8956D]">{h.date}</td>
                    <td className="px-4 py-3 text-[#8A7E73]">{d.toLocaleDateString("en-IN", { weekday: "short" })}</td>
                    <td className="px-4 py-3 font-medium text-[#3A322C]">{h.name}</td>
                    <td className="px-4 py-3"><span className={`text-xs font-bold px-2 py-0.5 rounded-full ${typeConf.color}`}>{typeConf.label}</span></td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold ${h.is_school_holiday ? "text-[#C2624E]" : "text-[#637C64]"}`}>
                        {h.is_school_holiday ? "Yes — excluded" : "No — included"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {isAdmin && (
                        <div className="flex gap-2">
                          <button onClick={() => { setForm({ ...h }); setModal({ mode: "edit", id: h.id }); setError(""); }} className="text-[#8A7E73] hover:text-[#E8956D]"><Edit size={14} /></button>
                          <button onClick={() => handleDelete(h.id)} className="text-[#8A7E73] hover:text-[#C2624E]"><Trash2 size={14} /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {holidays.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8A7E73]">No holidays added for {year}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {modal && (
        <Modal title={`${modal.mode === "create" ? "Add" : "Edit"} Holiday`} onClose={() => setModal(null)}>
          <div className="space-y-4">
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Holiday Name *</label>
              <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" placeholder="e.g. Diwali, Republic Day..." value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date *</label>
              <input type="date" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.date || ""} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Type</label>
              <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.holiday_type || "national"} onChange={e => setForm({ ...form, holiday_type: e.target.value })}>
                {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select></div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.is_school_holiday !== false} onChange={e => setForm({ ...form, is_school_holiday: e.target.checked })} />
              <span>Schools closed on this day (excludes from billing count)</span>
            </label>
            <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Notes</label>
              <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
            <div className="flex gap-3">
              <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
              <button onClick={handleSave} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Save</button>
            </div>
          </div>
        </Modal>
      )}
      {ConfirmModal}
    </div>
  );
}
